import { useEffect, useRef, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns, getJokerFieldType, normalizeNumberInput, parseLocaleNumber } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  AutoCompleteSelectServiceUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
} from '../Interface';
import { IssueMethod, IssueMethodList, isAutomaticIssueMethodAllowed } from '../../config/production';
import useGenerateJokerCols from '../../hooks/useGenerateJokerCols';

import useConfig from '../../context/ConfigContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import ItemDialog from '../Item/ItemDialog';
import masterDataService from '../../service/masterData.service';
import { Box } from '@mui/material';
import useParam from '../../context/ParamContext';
import { multiply, round } from 'mathjs';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';
const EditToolbar = (props) => {
  const { disabled, push, defaultItem } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultItem.id = uuidv4();
    defaultItem.isNew = true;
    defaultItem.quantityUnitId = null;
    await push(defaultItem);

    await validateField(`sourceListItem.${values.sourceListItem.length ?? 0}.itemCode`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const SourceListItem = ({
  disabled,
  defaultItem,
  autocompleteOptions,
  autocompleteData,
  jokerLineFields,
}) => {
  const { params } = useParam();
  const { config } = useConfig();
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const [itemDialogLineId, setItemDialogLineId] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDetailsByCode, setItemDetailsByCode] = useState({});

  const normalizeSingleItemResponse = (response) => {
    if (Array.isArray(response?.body)) {
      return response.body[0] ?? null;
    }

    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    return response?.body ?? response ?? null;
  };

  const rememberItemDetails = (item) => {
    if (!item?.itemCode) {
      return;
    }

    setItemDetailsByCode((prev) => {
      if (prev?.[item.itemCode]) {
        return prev;
      }

      return {
        ...prev,
        [item.itemCode]: item,
      };
    });
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) {
      return null;
    }

    const itemFromRows = (values?.sourceListItem ?? []).find((item) => item?.itemCode === itemCode)?.item;
    if (itemFromRows?.itemCode === itemCode) {
      return itemFromRows;
    }

    const cachedItem = itemDetailsByCode?.[itemCode];
    if (cachedItem) {
      return cachedItem;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      const item = normalizeSingleItemResponse(response);
      if (item) {
        rememberItemDetails(item);
      }
      return item;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const rowItems = (values?.sourceListItem ?? [])
      .map((item) => item?.item)
      .filter((item) => item?.itemCode);

    if (rowItems.length > 0) {
      setItemDetailsByCode((prev) => {
        const next = { ...prev };
        let changed = false;

        rowItems.forEach((item) => {
          if (!next[item.itemCode]) {
            next[item.itemCode] = item;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }

    const itemCodesToResolve = (values?.sourceListItem ?? [])
      .map((item) => item?.itemCode)
      .filter(
        (itemCode) => Boolean(itemCode) && !itemDetailsByCode?.[itemCode]
      );

    if (!itemCodesToResolve.length) {
      return;
    }

    let cancelled = false;

    Promise.all(itemCodesToResolve.map((itemCode) => getItemMetaByCode(itemCode)))
      .then((items) => {
        if (cancelled) {
          return;
        }

        items.filter(Boolean).forEach((item) => {
          rememberItemDetails(item);
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [values?.sourceListItem, itemDetailsByCode]);

  const getValueIndex = (values, id) => {
    return values.sourceListItem.findIndex((rc) => rc.id === id);
  };
  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'sourceListItem', getValueIndex);

  const notifyAutomaticIssueMethodRestricted = () => {
    addNotification({
      content: 'Automata kiadás csak Nincs készletkezelés módú cikknél állítható be.',
      type: NotificationType.WARNING,
    });
  };

  const getIssueMethodOptions = (itemCode) => {
    const item =
      (values?.sourceListItem ?? []).find((candidate) => candidate?.itemCode === itemCode)?.item ??
      itemDetailsByCode?.[itemCode];

    if (!item || isAutomaticIssueMethodAllowed(item)) {
      return IssueMethodList;
    }

    return IssueMethodList.filter((option) => option.value !== IssueMethod.BACKFLUSH);
  };

  const handleIssueMethodChange = (valueIndex, issueMethod) => {
    const itemCode = values.sourceListItem?.[valueIndex]?.itemCode;
    const item = values.sourceListItem?.[valueIndex]?.item ?? itemDetailsByCode?.[itemCode];

    if (issueMethod === IssueMethod.BACKFLUSH && item && !isAutomaticIssueMethodAllowed(item)) {
      notifyAutomaticIssueMethodRestricted();
      return false;
    }

    setFieldValue(`sourceListItem.${valueIndex}.issueMethod`, issueMethod);
    return true;
  };

  const handleItemDialogOpen = (rowId) => {
    setItemDialogLineId(rowId);
    setItemDialogOpen(true);
  };

  const selectItemFromDialog = async (itemCode) => {
    setItemDialogOpen(false);

    if (!itemCode || itemDialogLineId == null) {
      setItemDialogLineId(null);
      return;
    }

    const index = getValueIndex(values, itemDialogLineId);
    setItemDialogLineId(null);

    if (index === -1 || !values.sourceListItem?.[index]) return;

    // autocompletedata, autocompleteoptions frissítése, ha nincs benne a kiválasztott cikk

    const selectedItem = await getItemMetaByCode(itemCode);

    if (!selectedItem) {
      addNotification({
        content: 'Hiba történt a cikk adatainak lekérésekor!',
        type: NotificationType.ERROR,
      });
      return;
    }

    await handleItemChange(index, itemCode);
  };

  const handleItemChange = async (valueIndex, value) => {
    const selectedItem = await getItemMetaByCode(value);

    if (config.isManufacturedItemInsertable === false && selectedItem?.stockItemType === 'MANUFACTUREDPRODUCT') {
      addNotification({
        content: 'A forrásjegyzékbe gyártott cikk nem rögzíthető alapanyagként!',
        type: NotificationType.ERROR,
      });
      return false;
    }

    calculateWeight(valueIndex, values.sourceListItem[valueIndex].neededQuantity, selectedItem?.weight ?? null);
    setFieldValue(`sourceListItem.${valueIndex}.itemCode`, value);
    setFieldValue(`sourceListItem.${valueIndex}.item`, selectedItem ?? null);
    setFieldValue(`sourceListItem.${valueIndex}.quantityUnitId`, selectedItem?.quantityUnitId ?? null);

    if (
      values.sourceListItem?.[valueIndex]?.issueMethod === IssueMethod.BACKFLUSH &&
      selectedItem &&
      !isAutomaticIssueMethodAllowed(selectedItem)
    ) {
      setFieldValue(`sourceListItem.${valueIndex}.issueMethod`, IssueMethod.MANUAL);
      notifyAutomaticIssueMethodRestricted();
    }
    setFieldValue(`sourceListItem.${valueIndex}.weightPerItem`, selectedItem?.weight ?? null);
    return true;
  };

  const calculateWeight = (valueIndex, _qty, _weightPerItem) => {
    const qty = parseLocaleNumber(_qty);
    const weightPerItem = parseLocaleNumber(_weightPerItem);

    if (!Number.isFinite(qty) || !Number.isFinite(weightPerItem)) {
      setFieldValue(`sourceListItem.${valueIndex}.weight`, 0);
      return;
    }

    const weight = round(multiply(qty, weightPerItem), 2);
    setFieldValue(`sourceListItem.${valueIndex}.weight`, weight);
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'itemCode',
          headerName: 'Cikk',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = values.sourceListItem?.[valueIndex]?.itemCode;
            const item = values.sourceListItem?.[valueIndex]?.item ?? itemDetailsByCode?.[rowItemCode];
            return [item?.itemCode ?? rowItemCode, item?.itemName].filter(Boolean).join(' - ');
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = values.sourceListItem?.[valueIndex]?.itemCode;
            const item = values.sourceListItem?.[valueIndex]?.item ?? itemDetailsByCode?.[rowItemCode];
            return (
              <>
                <Field
                  name={`sourceListItem.${valueIndex}.itemCode`}
                  type="text"
                  variant="standard"
                  as={AutoCompleteSelectServiceUI}
                  fullWidth
                  helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.itemCode} />}
                  selectedValue={rowItemCode}
                  selectedLabelValue={[item?.itemCode ?? rowItemCode, item?.itemName].filter(Boolean).join(' - ')}
                  service={itemAutocompleteService}
                  serviceRequestBuilder={itemAutocompleteRequestBuilder}
                  onChange={async (_e, newVal) => {
                    const val = newVal?.value ?? null;

                    const changed = await handleItemChange(valueIndex, val);
                    if (!changed) return;

                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: val,
                    });
                  }}
                  onBlur={async () => await validateField(`sourceListItem.${valueIndex}.itemCode`)}
                />
                {params.SOURCELIST_ITEM_TAB_SHOW_ADD_ITEM_ICON === 'Y' && (
                  <Box>
                    <AddButtonUi onClick={() => handleItemDialogOpen(data.id)} />
                  </Box>
                )}
              </>
            );
          },
        },

        {
          field: 'neededQuantity',
          headerName: 'Szükséges mennyiség',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.sourceListItem?.[valueIndex]?.neededQuantity;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`sourceListItem.${valueIndex}.neededQuantity`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'neededQuantity'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.neededQuantity} />}
                onChange={(e) => {
                  const val = normalizeNumberInput(e.target?.value);

                  setFieldValue(`sourceListItem.${valueIndex}.neededQuantity`, val);
                  calculateWeight(valueIndex, val, values?.sourceListItem?.[valueIndex]?.weightPerItem);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.neededQuantity`)}
              />
            );
          },
        },

        {
          field: 'quantityUnitId',
          headerName: 'Me.',
          type: 'string',
          filterable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            const findedUnit = autocompleteData.quantityUnit.find(
              (u) => u.id === values.sourceListItem?.[valueIndex]?.quantityUnitId
            );
            return findedUnit?.code ?? '';
          },
        },

        {
          field: 'actions',
          type: 'actions',
          headerName: 'Szerk.',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            const valueIndex = getValueIndex(values, id);
            const findedI = values.sourceListItem?.[valueIndex]?.item ?? itemDetailsByCode?.[row.itemCode];
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={
                  disabled ||
                  (config.isManufacturedItemInsertable === false && findedI?.stockItemType === 'MANUFACTUREDPRODUCT')
                }
              />,
            ];
          },
        },

        {
          field: 'secondaryProduct',
          headerName: 'Melléktermék',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`sourceListItem.${valueIndex}.secondaryProduct`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.secondaryProduct} />}
                checked={values.sourceListItem?.[valueIndex]?.secondaryProduct ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`sourceListItem.${valueIndex}.secondaryProduct`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'whsCode',
          headerName: 'Raktár',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowWhsCode = values.sourceListItem?.[valueIndex]?.whsCode;
            const findedItem = autocompleteOptions?.warehouse.find((u) => u.value === rowWhsCode);

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowWhsCode = values.sourceListItem?.[valueIndex]?.whsCode;
            const findedItem = autocompleteOptions?.warehouse.find((u) => u.value === rowWhsCode);
            return (
              <Field
                name={`sourceListItem.${valueIndex}.whsCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.whsCode} />}
                selectedValue={rowWhsCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`sourceListItem.${valueIndex}.whsCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.warehouse}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.whsCode`)}
              />
            );
          },
        },
        {
          field: 'issueMethod',
          headerName: 'Kiadás módja',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = IssueMethodList.find(
              (u) => u.value === values.sourceListItem?.[valueIndex]?.issueMethod
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = IssueMethodList.find(
              (u) => u.value === values.sourceListItem?.[valueIndex]?.issueMethod
            );
            const issueMethodOptions = getIssueMethodOptions(values.sourceListItem?.[valueIndex]?.itemCode);
            return (
              <Field
                name={`sourceListItem.${valueIndex}.issueMethod`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.issueMethod} />}
                selectedValue={values.sourceListItem?.[valueIndex]?.issueMethod}
                value={findedItem}
                onChange={(_e, newVal) => {
                  const isChanged = handleIssueMethodChange(valueIndex, newVal?.value ?? null);
                  if (!isChanged) return;

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={issueMethodOptions}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.issueMethod`)}
              />
            );
          },
        },
        {
          field: 'weightPerItem',
          headerName: 'Súly / Tétel',
          type: 'number',
          editable: false,
          sortable: false,
        },
        {
          field: 'weight',
          headerName: 'Súly',
          type: 'number',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return values.sourceListItem?.[valueIndex]?.weight;
          },
        },
        ...jokerColumns,
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      jokerColumns,
      autocompleteData.quantityUnit,
      itemDetailsByCode,
      values.sourceListItem,
      errors,
      setFieldValue,
      validateField,
      disabled,
    ]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="sourceListItem">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.sourceListItem}
                  height={480}
                  columns={columns(remove)}
                  rows={values?.sourceListItem ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultItem,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    const valueIndex = getValueIndex(values, params.row.id);
                    const findedI = values.sourceListItem?.[valueIndex]?.item ?? itemDetailsByCode?.[params.row.itemCode];
                    if (
                      disabled ||
                      (config.isManufacturedItemInsertable === false &&
                        findedI?.stockItemType === 'MANUFACTUREDPRODUCT')
                    )
                      return;
                    focusedFieldRef.current = params.field;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
      <ItemDialog showDialog={itemDialogOpen} onClose={selectItemFromDialog} />
    </>
  );
};

export default SourceListItem;
